// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { createRefetchContainer } from "react-relay";
import withStyles from "@material-ui/core/styles/withStyles";
import { Tooltip, TextField, InputAdornment, CircularProgress, Button } from "@material-ui/core";
import {
  GroupAdd,
  Search,
  Warning,
  NotInterested,
  CheckCircle,
  RadioButtonUnchecked,
  Add,
} from "@material-ui/icons";
import { palette, greys } from "@clockwise/web-commons/src/styles/color.styles";
import { connect } from "react-redux";

// internal
import { IContainer, IProps, IState, TeamData } from "./TeamJoinCreate.d";
import { styles } from "./TeamJoinCreate.styles";
import { fragments, query } from "./TeamJoinCreate.gql";

// components
import { LargeControlGroup } from "@clockwise/web-commons/src/components/large-control-group";
import { TeamSettingsSearch } from "@components/teams-settings/team-settings-search";
import { TeamSearchInput } from "@components/team-search-input";
import { ProBadge } from "@clockwise/web-commons/src/ui/pro-badge";

// actions
import { snackBarOpen } from "@actions/snack-bar.actions";

// state
import { IReduxState } from "@reducers/root.reducer";

// util
import { isTeamInPaidSubscription } from "@util/pricing.util";
import { MAX_TEAM_SIZE } from "@util/team.util";
import { niceNameArrayFromPersonsWithCarryover } from "@clockwise/client-commons/src/util/event";
import { isClockwiseDomain } from "@clockwise/client-commons/src/util/email";
import { track, TrackingEvents } from "@util/analytics.util";
import { PostMessageManager } from "@util/post-message.util";

// mutations
import { createTeam, setPrimaryTeam, updateTeam, createTeamCalendar } from "@mutations";
import { AvatarRow } from "@components/avatar-row-new";
import { updateTeamCalendarState } from "@src/subscriptions/update-team-calendar-state/UpdateTeamCalendarState.subscription";

const teamCreatedEvents: Record<IProps["analyticsMode"], string> = {
  onboarding: TrackingEvents.ONBOARDING.ONBOARDING_TEAM_CREATED,
  settings: TrackingEvents.TEAMS.TEAM_SETTINGS_TEAM_CREATED,
  checkout: TrackingEvents.CHECKOUT.TEAM_JOIN_CREATE_MODAL_TEAM_CREATED,
  migration: TrackingEvents.TEAMS_MIGRATION.TEAM_CREATED,
};

const teamJoinedEvents: Record<IProps["analyticsMode"], string> = {
  onboarding: TrackingEvents.ONBOARDING.ONBOARDING_TEAM_JOINED,
  settings: TrackingEvents.TEAMS.TEAM_SETTINGS_TEAM_JOINED,
  checkout: TrackingEvents.CHECKOUT.TEAM_JOIN_CREATE_MODAL_TEAM_JOINED,
  migration: TrackingEvents.TEAMS_MIGRATION.TEAM_JOINED,
};

const teamTabChangedEvents: Record<IProps["analyticsMode"], string> = {
  onboarding: TrackingEvents.ONBOARDING.ONBOARDING_TEAM_TAB_CHANGED,
  settings: TrackingEvents.TEAMS.TEAM_SETTINGS_TEAM_TAB_CHANGED,
  checkout: TrackingEvents.CHECKOUT.TEAM_JOIN_CREATE_MODAL_TAB_CHANGED,
  migration: TrackingEvents.TEAMS_MIGRATION.TAB_CHANGED,
};

class TeamJoinCreateComponent extends React.Component<IProps, IState> {
  private changeSearchTimeout?: ReturnType<typeof setTimeout>;
  private hasInitialized = false;

  constructor(props: IProps) {
    super(props);

    this.state = {
      personMap: {},
      teamName: "",
      currentView: "create",
      joinMode: "off",
      selectedPersonIds: {},
      teams: [],
      query: "",
      teamNameIsValid: false,
      loading: false,
    };
  }

  /////////////
  // Helpers //
  /////////////
  private isMinimumRequiredInvitesSatisfied = (props: IProps) => {
    if (!props.minimumRequiredInvitesToCreate) {
      return true;
    }

    return Object.keys(this.state.selectedPersonIds).length >= props.minimumRequiredInvitesToCreate;
  };

  private isTeamNameValid = (teamName: string, teamNameIsValid: boolean) => {
    return teamNameIsValid && !!teamName.length;
  };

  private onCheck = (person: ISchema.IOrgPerson, checked: boolean) => {
    if (checked) {
      this.state.selectedPersonIds[person.personId] = person.primaryCalendarId;
    } else {
      delete this.state.selectedPersonIds[person.personId];
    }

    this.setState({ selectedPersonIds: this.state.selectedPersonIds });
    if (this.props.minimumRequiredInvitesToCreate) {
      this.props.onChangeValidity(
        this.isMinimumRequiredInvitesSatisfied(this.props) &&
          this.isTeamNameValid(this.state.teamName, this.state.teamNameIsValid),
      );
    }
  };

  private onInitSearch = ({
    nDisplayedInviteSuggestions,
  }: {
    nDisplayedInviteSuggestions: number;
  }) => {
    const { org } = this.props;

    if (!this.hasInitialized && this.props.onInit) {
      const suggestedTeams =
        (org.suggestedTeams &&
          org.suggestedTeams.__typename !== "GraphEntityError" &&
          org.suggestedTeams.list) ||
        [];

      this.props.onInit({
        nSuggestedTeams: suggestedTeams.length,
        nDisplayedTeams: this.state.teams.length,
        nDisplayedInviteSuggestions,
      });

      this.hasInitialized = true;
    }
  };

  private onChangeTeamName = (teamName: string, teamNameIsValid: boolean) => {
    this.props.onChangeValidity(
      this.isTeamNameValid(teamName, teamNameIsValid) &&
        this.isMinimumRequiredInvitesSatisfied(this.props),
    );
    if (this.props.onChangeTeamName) {
      this.props.onChangeTeamName(teamName);
    }
    this.setState({ teamName, teamNameIsValid });
  };

  private onChangeTeam = (index: number, control: TeamData) => {
    this.state.teams[index] = control;

    const selectedTeam = this.state.teams.find((team) => team.on);

    this.props.onChangeValidity(!!selectedTeam);
    if (selectedTeam && this.props.onChangeTeamName) {
      this.props.onChangeTeamName(selectedTeam.name);
    }
    this.setState({ teams: this.state.teams });
  };

  private onChangeSearchQuery = (e: any) => {
    const { org } = this.props;

    const query = (e.target && e.target.value) || "";

    if (this.changeSearchTimeout) {
      clearTimeout(this.changeSearchTimeout);
      delete this.changeSearchTimeout;
    }

    this.changeSearchTimeout = setTimeout(() => {
      this.props.relay.refetch(
        {
          query,
          orgRelayId: org.id,
          excludeMyTeams: !this.props.selectMode,
        },
        undefined,
        () => {
          this.setState({ loading: false });
        },
      );
    }, 500);

    this.setState({ query, loading: true });
  };

  private onSave = () => {
    const { org, mode, analyticsMode } = this.props;

    const yourPersonId =
      org.orgPersonForUserErrorable.__typename !== "GraphEntityError" &&
      org.orgPersonForUserErrorable.personId;

    const onTeam =
      (org.userTeams &&
        org.userTeams.__typename !== "GraphEntityError" &&
        org.userTeams.list &&
        !!org.userTeams.list.length) ||
      false;

    const userTeams =
      (org.userTeams?.__typename !== "GraphEntityError" && org.userTeams.list) || [];

    const isCreating = this.state.currentView === "create";

    const isSelecting = !isCreating && this.props.selectMode;

    const isSettingPrimary =
      !isCreating &&
      this.state.currentView === "join" &&
      (this.state.joinMode === "select-primary" || this.state.joinMode === "single-team") &&
      this.state.teams.length;

    const isJoining =
      !isSettingPrimary && this.state.currentView === "join" && this.state.teams.length;

    if (!isCreating && this.props.onPreSave) {
      this.props.onPreSave();
    }

    if (!yourPersonId) {
      if (this.props.onSave) {
        this.props.onSave();
      }
      return;
    }
    // if select mode, no need for any mutations, just tell us what was selected
    if (isSelecting) {
      if (this.props.onSave) {
        const teamId = this.state.teams.find((team) => team.on)!.id;
        this.props.onSave(teamId);
      }
    } else if (isCreating) {
      if (userTeams && userTeams.length >= 5) {
        this.props.snackBarOpen(
          "You have joined too many teams. Please remove yourself from a team if you wish to create a new one.",
        );
        return;
      }

      const teamMembers = Array.from([
        yourPersonId,
        ...Object.keys(this.state.selectedPersonIds),
      ]).map((personId) => ({ personId, role: ISchema.TeamMemberRole.Member }));

      createTeam(
        this.props.relay.environment,
        {
          teamMembers,
          orgRelayId: org.id,
          teamName: this.state.teamName,
          teamType: ISchema.TeamType.Other,
          setAsPrimary: (mode === "settings" && !onTeam) || mode === "onboarding",
        },
        (response) => {
          if (this.props.onPreSave) {
            this.props.onPreSave();
          }
          if (this.props.onSave) {
            this.props.onSave(response?.team?.teamId);
          }

          if (response) {
            updateTeamCalendarState(this.props.relay.environment, {
              orgRelayId: org.id,
              teamId: response.team.teamId,
            });

            createTeamCalendar(
              this.props.relay.environment,
              {
                orgRelayId: org.id,
                teamId: response.team.teamId,
              },
              () => null,
              () => null,
            );
          }

          if (!this.props.onPreSave) {
            this.props.snackBarOpen("Your team has been created!");
          }

          track(teamCreatedEvents[analyticsMode], {
            nInvited: teamMembers.length,
            teamName: this.state.teamName,
            teamId: response?.team.teamId,
            calendarIds: Object.values(this.state.selectedPersonIds),
          });
        },
        (error) => {
          if (
            mode === "onboarding" &&
            error.message.includes("Unable to create team - teammate is on too many teams")
          ) {
            this.props.snackBarOpen(
              "Sorry, we can't create your team - someone you invited is on too many teams",
            );
            if (this.props.onFail) {
              this.props.onFail();
            }
          } else if (!this.props.onPreSave) {
            this.props.snackBarOpen("Sorry, something went wrong creating your team");
          }
        },
      );
    } else if (isSettingPrimary) {
      const teamId = this.state.teams.find((team) => team.on)!.id;

      setPrimaryTeam(
        this.props.relay.environment,
        {
          teamId,
          orgRelayId: this.props.org.id,
        },
        () => {
          if (this.props.onSave) {
            this.props.onSave();
          }

          if (!this.props.onPreSave) {
            this.props.snackBarOpen("Your primary team has been set!");
          }
        },
        () => {
          if (!this.props.onPreSave) {
            this.props.snackBarOpen("Sorry, something went wrong setting your primary team");
          }
        },
      );

      userTeams.forEach((team) => {
        if (
          team.teamCalendarId &&
          team.settings &&
          team.settings.__typename !== "GraphEntityError" &&
          team.settings.manageTeamCalendar &&
          team.userSettings &&
          team.userSettings.__typename !== "GraphEntityError" &&
          team.userSettings.useTeamCalendar
        ) {
          PostMessageManager.toggleCalendar(team.teamCalendarId, true);
        }
      });

      if (analyticsMode === "onboarding" || analyticsMode === "migration") {
        track(
          mode === "onboarding"
            ? TrackingEvents.ONBOARDING.ONBOARDING_SET_PRIMARY_TEAM
            : TrackingEvents.TEAMS_MIGRATION.SET_PRIMARY_TEAM,
          { teamId },
        );
      }
    } else if (isJoining) {
      const team = this.state.teams.find((team) => team.on)!;
      const teamId = team.id;

      updateTeam(
        this.props.relay.environment,
        {
          teamId,
          orgRelayId: this.props.org.id,
          addedTeamMembers: [
            {
              personId: yourPersonId,
              role: ISchema.TeamMemberRole.Member,
            },
          ],
        },
        (response) => {
          if (this.props.onSave) {
            this.props.onSave(response?.team.teamId);
          }

          if (!this.props.onPreSave) {
            this.props.snackBarOpen("You have joined your team!");
          }

          if (
            response?.team.teamCalendarId &&
            response.team.settings &&
            response.team.settings.__typename !== "GraphEntityError" &&
            response.team.settings.manageTeamCalendar &&
            response.team.userSettings &&
            response.team.userSettings.__typename !== "GraphEntityError" &&
            response.team.userSettings.useTeamCalendar
          ) {
            PostMessageManager.toggleCalendar(response.team.teamCalendarId, true);
          }
        },
        () => {
          if (!this.props.onPreSave) {
            this.props.snackBarOpen("Sorry, something went wrong joining your team");
          }
        },
      );

      track(teamJoinedEvents[analyticsMode], { teamId, isSuggested: !!team.suggestedTeam });
    }
  };

  private onCreateTeamBtnClick = () => {
    this.props.onChangeValidity(
      this.isTeamNameValid(this.state.teamName, this.state.teamNameIsValid),
    );
    this.props.onChangeView("create");
    this.setState({ currentView: "create" });
  };

  ///////////////
  // Lifecycle //
  ///////////////
  private updateTeamsFromProps(props: IProps, stateChanges?: any, stateCallback?: () => void) {
    const { org, viewer, mode } = props;

    const userTeams =
      (org.userTeams?.__typename !== "GraphEntityError" && org.userTeams.list) || undefined;

    const teamQueryResult =
      (viewer.teamQueryResultErrorable?.__typename !== "GraphEntityError" &&
        viewer.teamQueryResultErrorable) ||
      undefined;

    const teamsQueryTeams = teamQueryResult?.teams;

    const suggestedTeams =
      (org.suggestedTeams &&
        org.suggestedTeams.__typename !== "GraphEntityError" &&
        org.suggestedTeams.list) ||
      [];

    const suggestedTeamsMap: Record<string, ISchema.ISuggestedTeam> = {};

    (suggestedTeams || []).forEach((st) => (suggestedTeamsMap[st.team.teamId] = st));

    const augmentedTeams = teamQueryResult &&
      teamQueryResult.query === "" && [
        ...suggestedTeams.map((st) => st.team),
        ...(teamsQueryTeams || []).filter((team) => !suggestedTeamsMap[team.teamId]),
      ];

    const primaryTeam =
      org.primaryTeam && org.primaryTeam.__typename !== "GraphEntityError" && org.primaryTeam;

    const personMap: Record<string, ISchema.IOrgPerson> = {};

    (
      (viewer.teamQueryResultErrorable &&
        viewer.teamQueryResultErrorable.__typename !== "GraphEntityError" &&
        viewer.teamQueryResultErrorable.teamMembers) ||
      []
    ).forEach((teamMember) => (personMap[teamMember.person.personId] = teamMember.person));

    userTeams &&
      userTeams.forEach((userTeam) =>
        [...(userTeam.teamMembers || []), ...(userTeam.invitedMembers || [])].forEach(
          (teamMember) => {
            personMap[teamMember.person.personId] = teamMember.person;
          },
        ),
      );

    const teams = (
      (mode !== "settings" && userTeams?.length && userTeams) ||
      augmentedTeams ||
      teamsQueryTeams ||
      []
    ).map((team, i) => {
      const teamPersons =
        ((team.invitedMembers || team.teamMembers) &&
          [...(team.invitedMembers || []), ...(team.teamMembers || [])].map(
            (teamMember) => teamMember.person.personId,
          )) ||
        team.teamRoleAndPersonIds?.map(({ personId }) => personId) ||
        [];

      const isDisabled =
        teamPersons.length >= MAX_TEAM_SIZE ||
        (mode === "checkout" && isTeamInPaidSubscription(props.org, team));
      return {
        team,
        on:
          (mode === "checkout" && primaryTeam && !isDisabled) ||
          (mode !== "settings" &&
            primaryTeam &&
            primaryTeam.teamId === team.teamId &&
            !isDisabled) ||
          ((mode === "settings" || !primaryTeam) && i === 0 && !!suggestedTeamsMap[team.teamId]),
        name: team.teamName,
        id: team.teamId,
        persons: teamPersons,
        suggestedTeam: suggestedTeamsMap[team.teamId],
        disabled: isDisabled,
      };
    });

    this.setState({ ...stateChanges, personMap, teams }, stateCallback);
  }

  public componentDidMount() {
    const { org, viewer, mode, forcedInitialView } = this.props;

    const userTeams =
      org.userTeams && org.userTeams.__typename !== "GraphEntityError" && org.userTeams.list;

    const teamQueryResult =
      viewer.teamQueryResultErrorable &&
      viewer.teamQueryResultErrorable.__typename !== "GraphEntityError" &&
      viewer.teamQueryResultErrorable;

    const teams = teamQueryResult && teamQueryResult.teams;

    const joinMode =
      mode !== "settings" && userTeams && userTeams.length
        ? userTeams.length === 1
          ? "single-team"
          : "select-primary"
        : teams && teams.length
        ? "normal"
        : "off";
    const currentView =
      forcedInitialView ||
      (mode !== "settings" && ((teams && teams.length) || (userTeams && userTeams.length))
        ? this.props.selectMode
          ? "select"
          : "join"
        : "create");

    this.props.onChangeView(currentView);
    this.props.onChangeJoinMode(joinMode);
    this.props.onChangeValidity(false);

    this.updateTeamsFromProps(
      this.props,
      {
        joinMode,
        currentView: joinMode === "off" ? "create" : currentView,
      },
      () => {
        const selectedTeam = this.state.teams.find((team) => team.on);

        if (
          (this.state.currentView === "join" || this.state.currentView === "select") &&
          selectedTeam
        ) {
          this.props.onChangeValidity(true);

          if (this.props.onChangeTeamName) {
            this.props.onChangeTeamName(selectedTeam.name);
          }
        }
      },
    );
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const query =
      this.props.viewer &&
      this.props.viewer.teamQueryResultErrorable.__typename !== "GraphEntityError" &&
      this.props.viewer.teamQueryResultErrorable.query;

    const nextQuery =
      nextProps.viewer &&
      nextProps.viewer.teamQueryResultErrorable.__typename !== "GraphEntityError" &&
      nextProps.viewer.teamQueryResultErrorable.query;

    if (!this.props.save && nextProps.save) {
      this.onSave();
    }

    if (query !== nextQuery) {
      this.updateTeamsFromProps(nextProps);
    }
  }

  ///////////////
  // Rendering //
  ///////////////

  private getSuggestedTeamReasons = (suggestedTeam: ISchema.ISuggestedTeam) => {
    return suggestedTeam.reasons
      .map((str) => {
        switch (str.type) {
          case ISchema.SuggestedTeamReasonType.NumOneOnOnesWithOverlap:
            return `You have ${str.value} one on one meetings with members in this team.`;
          case ISchema.SuggestedTeamReasonType.NumMeetingsWithOverlap:
          default:
            return `You have ${str.value} shared meetings with members in this team.`;
        }
      })
      .join("\n");
  };

  private renderCreateTeamHeader = () => {
    const { mode } = this.props;
    const { joinMode } = this.state;

    if (mode === "onboarding-manager") {
      return "Set up your team to make time together";
    } else if (joinMode === "off") {
      return "Create a new team";
    }
    return "What's the name of your team?";
  };

  private renderCreateTeam = () => {
    const { classes, org, viewer, mode } = this.props;
    const { joinMode } = this.state;

    const teamNameHeader =
      mode === "onboarding-manager" ? "Team name" : "What's the name of your team?";

    const descriptionText =
      mode === "onboarding-manager"
        ? "The best way to work together is for our schedules to do the same. Bring your team onboard so that Clockwise can make time for everyone."
        : "This could be the team you most closely identify with, the team you spend the most time with, or the team that is most important to your work.";
    return (
      <React.Fragment>
        <div style={{ position: "relative", zIndex: 2 }}>
          <div className={`${classes.header} extra-margin`}>{this.renderCreateTeamHeader()}</div>
          <div className={`${classes.body} extra-margin`}>{descriptionText}</div>
        </div>
        <TeamSettingsSearch
          mode="create"
          containerClassName={classes.teamSettingsSearchContainer}
          org={org}
          onInit={this.onInitSearch}
          viewer={viewer}
          team={null}
          onCheck={this.onCheck}
          defaultMembers={[]}
          invitedMembers={{}}
          render={({ renderSearch, renderResults }) => (
            <React.Fragment>
              <div className={classes.stickyContainer}>
                {joinMode === "off" ? (
                  <div className={classes.subHeader}>{teamNameHeader}</div>
                ) : null}
                <TeamSearchInput
                  org={org}
                  onChange={this.onChangeTeamName}
                  initialTeamName={this.state.teamName}
                />
                <div className={`${classes.subHeader} extra-margin pre-extra-margin`}>
                  Invite your teammates
                </div>
                {renderSearch()}
              </div>
              {renderResults()}
            </React.Fragment>
          )}
        />
      </React.Fragment>
    );
  };

  private renderControlContent = (control: TeamData) => {
    const { classes, org } = this.props;
    const { joinMode } = this.state;

    let overflowCount = 0;

    const persons = control.persons
      .map((personId) => this.state.personMap[personId])
      .sort((a, b) => {
        if (a.isYou && !b.isYou) {
          return -1;
        } else if (b.isYou && !a.isYou) {
          return 1;
        }

        return 0;
      })
      .filter((person, i) => {
        if (person && i < 50) {
          return true;
        }

        overflowCount++;
        return false;
      });

    const { niceNameArray, carryover } = niceNameArrayFromPersonsWithCarryover(
      persons,
      undefined,
      28,
      undefined,
      true,
      true,
      overflowCount,
    );

    const niceCarryover =
      carryover.length === 1
        ? carryover
        : carryover.length > 0
        ? [
            ...carryover.slice(0, carryover.length - 1),
            `and ${carryover[carryover.length - 1]}`,
          ].join(", ")
        : undefined;

    const niceNames = niceNameArray.map((name, i) => {
      if (i < niceNameArray.length - 1 || !niceCarryover) {
        return <span key={i}>{name}</span>;
      }

      return (
        <Tooltip title={niceCarryover}>
          <span key={i} className={classes.underline}>
            {name}
          </span>
        </Tooltip>
      );
    });

    const showWarning = control.disabled && this.props.mode !== "checkout";

    return (
      <div
        className={showWarning ? classes.controlWithWarning : ""}
        style={{ width: "100%", padding: 13 }}
      >
        <div className={classes.control}>
          {control.disabled ? (
            <Tooltip title="This team is already on the Pro plan.">
              <NotInterested />
            </Tooltip>
          ) : control.on ? (
            <CheckCircle />
          ) : (
            <RadioButtonUnchecked />
          )}
          <div className={classes.controlContent}>
            <div className="title-names">
              <div className="title">
                {control.name}
                {joinMode !== "select-primary" && control.suggestedTeam && (
                  <Tooltip title={this.getSuggestedTeamReasons(control.suggestedTeam)}>
                    <span className="suggested">Suggested</span>
                  </Tooltip>
                )}
                {this.props.mode === "checkout" &&
                  isTeamInPaidSubscription(this.props.org, control.team) && (
                    <ProBadge className={classes.badgePro} />
                  )}
              </div>
              {niceNames.length > 0 ? <div className="persons-names">{niceNames}</div> : ""}
            </div>
            <div className="avatars">
              <AvatarRow
                org={org}
                orgPersons={persons}
                extraOverflowCount={overflowCount}
                size={28}
              />
            </div>
          </div>
        </div>
        {showWarning && (
          <div className={classes.teamSizeWarning}>
            <Warning style={styles.warningIcon} />
            <span className={classes.warningLabel}>
              This team has reached its maximum size of 50 people
            </span>
          </div>
        )}
      </div>
    );
  };

  private renderJoinTeam = () => {
    const { joinMode, loading, query, teams } = this.state;
    const { mode, classes } = this.props;

    const showSearch = joinMode === "normal" && (query || teams.length >= 5 || (!query && loading));

    const suggestedTeams =
      (this.props.org.suggestedTeams &&
        this.props.org.suggestedTeams.__typename !== "GraphEntityError" &&
        this.props.org.suggestedTeams.list) ||
      [];

    const showCreateTeamButton = joinMode === "normal" && mode === "onboarding";
    return (
      <React.Fragment>
        <div className={`${classes.header} extra-margin`}>
          {mode === "checkout"
            ? "Which team would you like to upgrade?"
            : joinMode === "normal"
            ? "Which team are you on?"
            : "Which team is your primary team?"}
        </div>
        <div className={`${classes.body} extra-margin`}>
          {mode === "checkout" ? (
            "Not ready to pay for your entire organization? You can start with upgrading one of your teams."
          ) : joinMode === "normal" ? (
            <React.Fragment>
              {teams.length > 1
                ? "We found several teams within your organization."
                : "We found a team existing within your organization."}{" "}
              Join the team that you most closely identify with, or create a new team if your
              primary team isn't listed.
            </React.Fragment>
          ) : (
            <React.Fragment>
              {teams.length === 1
                ? `Your coworkers have added you to one team within your organization. `
                : teams.length === 2
                ? `Your coworkers have added you to two teams within your organization. `
                : `Your coworkers have added you to several teams within your organization. `}
              Let us know which one you most closely identify with.
            </React.Fragment>
          )}
        </div>

        {showSearch ? (
          <TextField
            autoFocus
            className={classes.teamSearchTextField}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={24} />
                </InputAdornment>
              ) : null,
            }}
            placeholder="Search for a team..."
            variant="standard"
            value={query}
            onChange={this.onChangeSearchQuery}
          />
        ) : null}

        {showCreateTeamButton ? (
          <Button
            fullWidth
            onClick={this.onCreateTeamBtnClick}
            style={{
              ...styles.createTeamBtn,
              color: suggestedTeams.length > 0 ? greys.slate : palette.green,
            }}
          >
            <Add style={styles.createTeamBtnIcon} />
            <div style={styles.createTeamBtnText}>Create a new team</div>
          </Button>
        ) : null}

        {this.state.teams.length ? (
          <LargeControlGroup
            controls={this.state.teams}
            renderControlContent={this.renderControlContent}
            onChange={this.onChangeTeam}
            allowPointerEvents
            overrideContent
          />
        ) : (
          <div className={classes.noResults}>No results found</div>
        )}
      </React.Fragment>
    );
  };

  private renderCurrentView = () => {
    const { currentView } = this.state;

    switch (currentView) {
      case "join":
      case "select":
        return this.renderJoinTeam();
      case "create":
      default:
        return this.renderCreateTeam();
    }
  };

  private maybeRenderPills = () => {
    if (
      (this.props.mode !== "checkout" &&
        (this.state.joinMode === "off" || this.state.joinMode === "select-primary")) ||
      this.props.mode == "onboarding"
    ) {
      return null;
    }

    const { classes, analyticsMode } = this.props;
    const { currentView } = this.state;

    return (
      <div className={classes.pills}>
        {this.props.selectMode ? (
          <div
            className={`pill${currentView === "select" ? " active" : ""}`}
            onClick={() => {
              this.props.onChangeValidity(!!this.state.teams.find((team) => team.on));
              this.props.onChangeView("select");
              this.setState({ currentView: "select" });

              track(teamTabChangedEvents[analyticsMode], { currentView: "select" });
            }}
          >
            Select a team
          </div>
        ) : (
          <div
            className={`pill${currentView === "join" ? " active" : ""}`}
            onClick={() => {
              this.props.onChangeValidity(!!this.state.teams.find((team) => team.on));
              this.props.onChangeView("join");
              this.setState({ currentView: "join" });

              track(teamTabChangedEvents[analyticsMode], { currentView: "join" });
            }}
          >
            Join a team
          </div>
        )}
        <div
          className={`pill${currentView === "create" ? " active" : ""}`}
          onClick={() => {
            this.props.onChangeValidity(
              this.isTeamNameValid(this.state.teamName, this.state.teamNameIsValid),
            );
            this.props.onChangeView("create");
            this.setState({ currentView: "create" });

            track(teamTabChangedEvents[analyticsMode], { currentView: "create" });
          }}
        >
          <GroupAdd /> Create a new team
        </div>
      </div>
    );
  };

  private maybeRenderDebugMenu = () => {
    const { org, classes } = this.props;

    if (!isClockwiseDomain(org.primaryOrgCalendar)) {
      return null;
    }

    return (
      <div className={classes.debugMenu}>
        <Button
          fullWidth
          onClick={() => {
            this.setState({ currentView: "create", joinMode: "off" });
          }}
        >
          Seed
        </Button>
        <Button
          fullWidth
          onClick={() => {
            this.setState({ currentView: "join", joinMode: "normal" });
          }}
        >
          Join
        </Button>
        <Button
          fullWidth
          onClick={() => {
            this.setState({ currentView: "select", joinMode: "normal" });
          }}
        >
          Select
        </Button>
      </div>
    );
  };

  public render() {
    return (
      <React.Fragment>
        {this.maybeRenderPills()}
        {this.renderCurrentView()}
        {this.maybeRenderDebugMenu()}
      </React.Fragment>
    );
  }
}

/////////////////
// With Styles //
/////////////////

const TeamJoinCreateWithStyles = withStyles(styles)(TeamJoinCreateComponent);

///////////
// Redux //
///////////

function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const TeamJoinCreateWithRedux = connect(mapStateToProps, {
  snackBarOpen,
})(TeamJoinCreateWithStyles);

///////////
// Relay //
///////////

export const TeamJoinCreate = createRefetchContainer(TeamJoinCreateWithRedux, fragments, query);
